import { DestroyRef, Injectable, inject } from '@angular/core';
import { combineLatest, Observable, of, ReplaySubject } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FuseNavigationItem } from '@fuse/components/navigation';
import { AgaveAuthService } from 'tq-agave';

import { Navigation } from 'app/core/navigation/navigation.types';
import { standardNavigation } from './navigation.data';
import { UserService } from '../user/user.service';
import { MacroService } from 'app/macro.service';
import { User } from 'app/core/user/user.types';

@Injectable({ providedIn: 'root' })
export class NavigationService {
    private _agaveAuthService = inject(AgaveAuthService);
    private _userService = inject(UserService);
    private _macroService = inject(MacroService);
    private _destroyRef = inject(DestroyRef);

    /**
     * Sorgente "mutabile" della navigazione.
     * - Di default usa la standardNavigation.
     * - Può essere sostituita a runtime (es. dopo fetch da DB) tramite setter `currentNavigation`.
     *
     * Nota: su questa base vengono poi applicati:
     * 1) filtro clearance (feature flag / permessi via AgaveAuthService)
     * 2) sostituzione macro + filtro item con macro mancanti
     */
    private _currentNavigation: FuseNavigationItem[] = standardNavigation;

    /**
     * Stream pubblico della navigazione calcolata.
     * ReplaySubject(1) garantisce che chi si sottoscrive riceva subito l'ultimo valore emesso.
     */
    private _navigation = new ReplaySubject<Navigation>(1);

    constructor() {
        // Aggiorna automaticamente la navigation ogni volta che cambiano user o macroMap
        this.subscribeForAutoReload();
    }

    get navigation$(): Observable<Navigation> {
        return this._navigation.asObservable();
    }

    /**
     * Getter/Setter per sostituire dinamicamente la navigazione base.
     * Esempio d'uso: dopo una chiamata al backend che restituisce nuovi item aggiorno e ricalcolo la navigation.
     */
    get currentNavigation(): FuseNavigationItem[] {
        return this._currentNavigation;
    }

    set currentNavigation(value: FuseNavigationItem[]) {
        this._currentNavigation = value;
        this.updateNavigation();
    }

    /**
     * Ricalcola e pubblica la navigation usando gli snapshot sincroni correnti:
     * - macroMap è lo snapshot corrente delle macro
     *
     * Restituisce l'oggetto Navigation calcolato e lo emette nello stream.
     */
    updateNavigation(): Navigation {
        const nav = this.buildNavigation(
            this._userService.userOrNull,
            this._macroService.macroMap
        );
        this._navigation.next(nav);
        return nav;
    }

    /**
     * Costruisce la navigation finale a partire dalla navigation base (currentNavigation).
     *
     * Pipeline:
     * 1) filterClearanceNavigation: rimuove item che hanno una clearance non soddisfatta da AgaveAuthService
     * 2) applyMacrosAndFilter: sostituisce macro in tutti i campi stringa e scarta item con macro mancanti
     *      {macro1: '', macro2: 'test'} -> vedo tutti gli elementi con macro1 o macro2
     *      {macro1: ''} -> vedo tutti gli elementi con macro1, ma scarto quelli con macro2 perché non risolvibile
     */
    buildNavigation(user: User | null, macrosIn: Record<string, string>): Navigation {
        let items = this.filterClearanceNavigation(this._currentNavigation ?? standardNavigation);

        items = this.applyMacrosAndFilter(items, macrosIn);

        return {
            compact: items,
            default: items,
            futuristic: items,
            horizontal: items
        };
    }

    /**
     * Applica il filtro "clearance" alla navigation.
     * Regola: se item.clearance è valorizzato e AgaveAuthService lo valuta a false => item escluso
     */
    private filterClearanceNavigation(items: FuseNavigationItem[]): FuseNavigationItem[] {
        return items
            .filter(item => {
                if (item.clearance && !this._agaveAuthService.getBooleanParameterValue(item.clearance)) {
                    return false;
                }
                return true;
            })
            .map(item => ({
                ...item,
                children: item.children ? this.filterClearanceNavigation(item.children) : []
            }));
    }

    /**
     * Sostituisce le macro in QUALUNQUE campo stringa dell'item (anche annidato: meta/classes/badge, ecc.)
     * e scarta l'intero item se anche UNA macro presente in un testo non esiste nella macroMap.
     *
     * Regole:
     * - se una stringa contiene macro e almeno una non è risolvibile => item eliminato
     * - i children vengono processati con la stessa logica
     * - non-stringhe (number/boolean/function/null/undefined) vengono lasciate invariate
     */
    private applyMacrosAndFilter(
        items: FuseNavigationItem[],
        macroMap: Record<string, string>
    ): FuseNavigationItem[] {

        /**
         * Sostituisce le macro in una stringa.
         * Se la stringa contiene macro mancanti -> ritorna null (segnale di scarto item).
         */
        const replaceMacrosInStringOrNull = (text: string): string | null => {
            const macros = this._macroService.getMacroFromText(text);

            // Se anche UNA macro non esiste -> elimina item
            if (macros.some(m => !this._macroService.existsMacroInMacroMap(m, macroMap))) {
                return null;
            }

            return this._macroService.replaceMacroInText(text, macroMap);
        };

        /**
         * Visita ricorsiva su qualunque valore:
         * - string: sostituisce macro o ritorna null se macro mancanti
         * - array: processa ogni elemento; se uno torna null -> null
         * - object: processa tutte le proprietà; se una torna null -> null
         * - altro: ritorna invariato
         */
        const replaceInAnyOrNull = (value: unknown): unknown | null => {
            if (typeof value === 'string') {
                return replaceMacrosInStringOrNull(value);
            }

            if (Array.isArray(value)) {
                const res: unknown[] = [];
                for (const el of value) {
                    const r = replaceInAnyOrNull(el);
                    if (r === null) return null;
                    res.push(r);
                }
                return res;
            }

            if (value && typeof value === 'object') {
                const res: Record<string, unknown> = {};
                for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
                    const r = replaceInAnyOrNull(v);
                    if (r === null) return null;
                    res[k] = r;
                }
                return res;
            }

            // numeri, boolean, funzioni, null, undefined, ecc.
            return value;
        };

        /**
         * Processa un singolo item:
         * 1) sostituisce le macro in tutti i campi stringa (deep)
         * 2) processa i children ricorsivamente e filtra quelli null
         */
        const processItem = (item: FuseNavigationItem): FuseNavigationItem | null => {
            const replacedAny = replaceInAnyOrNull(item);
            if (replacedAny === null) return null;

            const replaced = replacedAny as FuseNavigationItem;

            const children = replaced.children
                ? replaced.children
                    .map(processItem)
                    .filter((x): x is FuseNavigationItem => x !== null)
                : undefined;

            return { ...replaced, children };
        };

        return items
            .map(processItem)
            .filter((x): x is FuseNavigationItem => x !== null);
    }

    /**
     * Bootstrap/resolver:
     * deve restituire subito una Navigation usando gli snapshot sincroni correnti.
     * Utile per inizializzare l'app con una navigation "coerente"
     */
    loadBeforeApplicationStart(): Observable<Navigation> {
        const nav = this.updateNavigation();
        return of(nav);
    }

    /**
     * Auto-reload:
     * ogni volta che cambiano user$ o macroMap$ ricalcola la navigation e la riemette.
     * La subscription viene chiusa automaticamente al destroy grazie a takeUntilDestroyed.
     */
    private subscribeForAutoReload(): void {
        combineLatest([this._userService.user$, this._macroService.macroMap$])
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe(([user, macros]) => {
                this._navigation.next(this.buildNavigation(user, macros));
            });
    }
}
